import { Spin } from 'antd'
import { GeckoBinnedDataBuffer, useCdfRawValues } from 'features/dataLayers'
import {
  FigureSelectionArea,
  measureToXBinIndex,
  measureToYBinIndex,
  xBinIndexToMeasure,
  yBinIndexToMeasure,
} from 'features/fde'
import { CriticalProfilePoint } from 'features/indicationTriage/cards/criticalProfile/criticalProfileApi'
import {
  LABEL_WIDTH,
  SectionContainer,
} from 'features/indicationTriage/cards/ffsCalc/FfsCalcLayout'
import { FfsParameterField } from 'features/indicationTriage/cards/ffsCalc/FfsParameterField'
import { LABEL_RSFA } from 'features/indicationTriage/cards/ffsCalc/levelOne/FfsFinalAcceptabilityCriteraSection'
import { useIndicationData } from 'features/indicationTriage/IndicationContext'
import _ from 'lodash'
import React, { Dispatch, FC, SetStateAction, useMemo, useState } from 'react'
import {
  GeckoComponentCoords,
  GeckoComponentDataFrame,
  GeckoMeasurement,
  GeckoMeasurementUnit,
} from 'types'

import { computeRsfs, Rsf, RsfTable } from './RsfTable'

export interface FfsCtpAverageCalcProps {
  isLoading: boolean
  area: FigureSelectionArea | undefined
  rsfs: ReadonlyArray<Rsf>
  minRsf: Rsf | undefined
  passesFac: boolean
  rsfa: GeckoMeasurement | null
  setRsfa: Dispatch<SetStateAction<GeckoMeasurement | null>>
}

function getMinPointIndex(points: (number | undefined)[]): number | undefined {
  if (!points?.length) {
    return undefined
  }
  let minIdx = -1
  points.forEach(
    (point, idx) =>
      (minIdx =
        _.isNil(points[minIdx]) || (point && point < points[minIdx]!)
          ? idx
          : minIdx)
  )
  return minIdx < 0 ? undefined : minIdx
}

function computeCriticalPath(
  area: FigureSelectionArea,
  cdf: GeckoComponentDataFrame,
  cdfData: GeckoBinnedDataBuffer['values'],
  axis: 'x' | 'y'
): ReadonlyArray<CriticalProfilePoint> {
  const xStart = area.x.start
  const yStart = area.y.start
  const xEnd = area.x.end
  const yEnd = area.y.end

  const xBinStart = Math.round(measureToXBinIndex(xStart, cdf))
  const xBinEnd = Math.round(measureToXBinIndex(xEnd, cdf))
  const yBinStart = Math.round(measureToYBinIndex(yStart, cdf))
  const yBinEnd = Math.round(measureToYBinIndex(yEnd, cdf))

  const getPoint: (x: number, y: number) => number | undefined = (x, y) => {
    return cdfData.at(y * cdf.data.binning.x_bin_count + x)
  }

  const idxRange =
    axis === 'x' ? _.range(xBinStart, xBinEnd) : _.range(yBinStart, yBinEnd)
  const getSegment: (idx: number) => (number | undefined)[] =
    axis === 'x'
      ? (x) => _.range(yBinStart, yBinEnd).map((y) => getPoint(x, y))
      : (y) => _.range(xBinStart, xBinEnd).map((x) => getPoint(x, y))

  const cdfValueUnit = cdf.data.params.measurement_unit
  return idxRange
    .map(getSegment)
    .map((segment, indexPos) => {
      const valuePos = getMinPointIndex(segment)
      const value = !_.isNil(valuePos) ? segment[valuePos] : undefined
      if (_.isNil(valuePos) || _.isNil(value) || _.isNaN(value)) {
        return undefined
      }
      const meas = _.isNil(value)
        ? undefined
        : GeckoMeasurement.CreateFromValue(value, cdfValueUnit)

      const xMeas =
        axis === 'x'
          ? xBinIndexToMeasure(idxRange[indexPos], cdf)
          : xBinIndexToMeasure(xBinStart + valuePos, cdf)
      const yMeas =
        axis === 'x'
          ? yBinIndexToMeasure(yBinStart + valuePos, cdf)
          : yBinIndexToMeasure(idxRange[indexPos], cdf)
      return {
        coords: GeckoComponentCoords.CreateFromMeasurements(
          cdf.tags.component,
          xMeas,
          yMeas
        ),
        value: meas,
      }
    })
    .filter((item): item is NonNullable<typeof item> => !_.isNil(item))
}

type HookProps = {
  innerDiameter: GeckoMeasurement | null
  area: FigureSelectionArea | undefined
  tc: GeckoMeasurement | null
  fcaMl: GeckoMeasurement | null
  nominal: GeckoMeasurement | null
  longitudinalAxis: 'x' | 'y'
  isLoading: boolean
}

export function useFfsCtpAverageCalcProps({
  area,
  tc,
  fcaMl,
  longitudinalAxis,
  isLoading,
  innerDiameter,
  nominal,
}: HookProps): FfsCtpAverageCalcProps {
  const { relevantThicknessCDF: cdf } = useIndicationData()
  const { buffer: cdfData, isLoading: cdfDataLoading } = useCdfRawValues(cdf)
  const criticalPath = useMemo(
    () =>
      cdf && cdfData && area && fcaMl
        ? computeCriticalPath(area, cdf, cdfData.values, longitudinalAxis).map(
            (point) => ({
              ...point,
              value: point.value
                ? GeckoMeasurement.CreateFromValue(
                    point.value.num - fcaMl.toNumber(point.value.unit),
                    point.value.unit
                  )
                : undefined,
            })
          )
        : [],
    [area, cdf, cdfData, fcaMl, longitudinalAxis]
  )

  const rsfs = useMemo(
    () =>
      !_.isNil(tc) &&
      !_.isNil(nominal) &&
      !_.isNil(fcaMl) &&
      !_.isNil(innerDiameter)
        ? computeRsfs({
            criticalPath,
            tc,
            nominal,
            axis: longitudinalAxis,
            fca: fcaMl,
            innerDiameter,
          })
        : [],
    [criticalPath, fcaMl, innerDiameter, longitudinalAxis, nominal, tc]
  )

  const minRsf = _.minBy(
    rsfs,
    (rsf) =>
      rsf.rsf?.toNumber(GeckoMeasurementUnit.Ratio) ?? Number.POSITIVE_INFINITY
  )
  const [rsfa, setRsfa] = useState<GeckoMeasurement | null>(
    GeckoMeasurement.CreateFromValue(0.9, GeckoMeasurementUnit.Ratio)
  )
  return useMemo(
    () => ({
      area,
      longitudinalAxis,
      criticalPath,
      isLoading: isLoading || cdfDataLoading,
      rsfs,
      minRsf,
      passesFac: !!minRsf?.rsf && !!rsfa && minRsf.rsf.num >= rsfa.num,
      rsfa,
      setRsfa,
    }),
    [
      area,
      cdfDataLoading,
      criticalPath,
      isLoading,
      longitudinalAxis,
      minRsf,
      rsfa,
      rsfs,
    ]
  )
}

const RawFfsCtpAverageCalc: FC<FfsCtpAverageCalcProps> = ({
  area,
  isLoading,
  rsfs,
  minRsf,
  rsfa,
  setRsfa,
}) => {
  return (
    <Spin spinning={isLoading}>
      <RsfTable rsfs={rsfs} />
      <SectionContainer>
        {area && (
          <>
            <FfsParameterField
              label={LABEL_RSFA}
              labelTooltip='Acceptable Remaining Strength Factor'
              labelWidth={LABEL_WIDTH}
              value={rsfa}
              setValue={setRsfa}
              measurementType='ratio'
              min={GeckoMeasurement.CreateFromValue(
                0,
                GeckoMeasurementUnit.Ratio
              )}
              max={GeckoMeasurement.CreateFromValue(
                1,
                GeckoMeasurementUnit.Ratio
              )}
              step={0.05}
            />
            <FfsParameterField
              label='RSF'
              labelTooltip='Minimum RSF from the CTP sections'
              labelWidth={LABEL_WIDTH}
              value={minRsf?.rsf ?? null}
              measurementType='ratio'
            />
          </>
        )}
      </SectionContainer>
    </Spin>
  )
}

export const FfsCtpAverageCalc = React.memo(RawFfsCtpAverageCalc)
